import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { useTheme, MenuItem, MenuList } from '@mui/material';
import { Box } from '../Box';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export type PresetPrecision = 'minute' | 'day';

export type Preset = {
  id: string;
  label: string;
  getValue: (now: Date, precision?: PresetPrecision) => [Date, Date];
};

interface PresetsProps {
  presets: Preset[];
  timezone: 'local' | 'utc';
  onSelect: (dates: [Dayjs, Dayjs], preset: Preset) => void;
  precision?: PresetPrecision;
  selectedPresetId?: string | null;
}

export const defaultPresets: Preset[] = [
  {
    id: 'last_day',
    label: 'Last 24 Hours',
    getValue: now => {
      return [
        dayjs(now).subtract(1, 'day').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    id: 'last_week',
    label: 'Last Week',
    getValue: (now, precision) => {
      return [
        dayjs(now).subtract(1, 'week').startOf(precision).toDate(),
        dayjs(now).endOf(precision).toDate(),
      ];
    },
  },
  {
    id: 'last_month',
    label: 'Last Month',
    getValue: (now, precision) => {
      return [
        dayjs(now).subtract(1, 'month').startOf(precision).toDate(),
        dayjs(now).endOf(precision).toDate(),
      ];
    },
  },
  {
    id: 'last_three_months',
    label: 'Last 3 Months',
    getValue: (now, precision) => {
      return [
        dayjs(now).subtract(3, 'months').startOf(precision).toDate(),
        dayjs(now).endOf(precision).toDate(),
      ];
    },
  },
  {
    id: 'last_six_months',
    label: 'Last 6 Months',
    getValue: (now, precision) => {
      return [
        dayjs(now).subtract(6, 'months').startOf(precision).toDate(),
        dayjs(now).endOf(precision).toDate(),
      ];
    },
  },
];

export const Presets: React.FC<PresetsProps> = ({
  presets,
  precision,
  timezone,
  onSelect,
  selectedPresetId,
}) => {
  const theme = useTheme();
  const componentTokens = theme.palette?.componentTokens;

  const now = React.useMemo(() => {
    return (timezone === 'local' ? dayjs() : dayjs().utc()).toDate();
  }, [timezone]);

  return (
    <Box
      sx={{
        p: '6px',
        border: '1px solid',
        borderRight: 'none',
        borderColor: componentTokens?.calendar.colors.container.stroke.default,
        backgroundColor: componentTokens?.calendar.colors.container.background.default,
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
        alignSelf: 'stretch',
      }}
    >
      <MenuList sx={{ padding: 0 }}>
        {presets.map(preset => {
          const isSelected = selectedPresetId === preset.id;

          return (
            <MenuItem
              key={preset.id}
              sx={{
                minWidth: '150px',
              }}
              selected={isSelected}
              aria-selected={isSelected}
              aria-label={preset.label}
              onClick={() => {
                const value = preset.getValue(now, precision);
                if (timezone === 'utc') {
                  onSelect([dayjs.utc(value[0]), dayjs.utc(value[1])], preset);
                } else {
                  onSelect([dayjs(value[0]), dayjs(value[1])], preset);
                }
              }}
            >
              {preset.label}
            </MenuItem>
          );
        })}
      </MenuList>
    </Box>
  );
};

export default Presets;
